import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import type { RowObject } from '@highcharts/dashboards/es-modules/Data/DataTable.js';
import {
    formatDashboardValue,
    isAggregationConfigured,
    ratioValueFormat,
} from '@h20-shared/dashboards';
import { gridLocale } from '@h20-shared/grids/grid-context';
import { BadgeCellRendererComponent } from '@h20-shared/table-component/cell-renderer/individual/badge-cell-renderer/badge-cell-renderer.component';
import type { BadgeCellRendererParamsTypes } from '@h20-shared/table-component/cell-renderer/individual/badge-cell-renderer/badge-cell-renderer.component';
import { GRID_MAIN_VALUE_KEY } from '../dashboard-grid.config';
import { isRatioMainValue } from '../dashboard-grid.group-by';
import type { GridMainValueConfig } from '../dashboard-grid.schema';

const NO_DATA_BADGE = {
    text: 'Dashboard.Grid.NoData',
    badgeColor: 'gray',
    tooltip: 'Dashboard.Grid.NoDataTooltip',
} satisfies BadgeCellRendererParamsTypes;

export function buildMainValueColumn(
    mainValue: GridMainValueConfig,
    translate: (i18nKey: string) => string
): ColDef<RowObject> | undefined {
    if (!isAggregationConfigured(mainValue.numerator)) {
        return undefined;
    }

    // The settings alone decide the format, so it is picked here rather than once per drawn cell.
    const format = isRatioMainValue(mainValue)
        ? ratioValueFormat(mainValue.format)
        : mainValue.format;

    return {
        headerName: mainValue.label || translate('Dashboard.Grid.UntitledColumn'),
        field: GRID_MAIN_VALUE_KEY,
        colId: GRID_MAIN_VALUE_KEY,
        valueFormatter: ({ value, context }: ValueFormatterParams<RowObject, unknown>) =>
            typeof value === 'number' || typeof value === 'string'
                ? (formatDashboardValue(value, format, gridLocale(context)) ?? '')
                : '',
        cellRendererSelector: ({ value }: ICellRendererParams<RowObject, unknown>) => {
            if (typeof value === 'number' || typeof value === 'string') {
                return undefined;
            }
            return {
                component: BadgeCellRendererComponent,
                params: { badgeParams: NO_DATA_BADGE },
            };
        },
    };
}
